// @mui
import { alpha, styled } from "@mui/material/styles";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
// hooks
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
// icons
import { LuCalendar, LuFilePlus, LuHome } from "react-icons/lu";
import { RiFileList3Line } from "react-icons/ri";
import { MdEmail, MdMeetingRoom, MdCampaign, MdPoll, MdAccessTime, MdSchool } from "react-icons/md";
import { paths } from "../../../../routes/paths";
import { useAuth } from "../../../../hooks";
import { useMessageListQuery } from "../../../../services/message.api";

export const StyledTabs = styled(Tabs)(({ theme }) => ({
  display: "flex",
  position: "fixed",
  bottom: 12,
  left: 12,
  right: 12,
  zIndex: theme.zIndex.modal + 1,
  width: "auto",
  whiteSpace: "unset !important",
  backdropFilter: "blur(24px)",
  WebkitBackdropFilter: "blur(24px)",
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 4px 24px rgba(0, 0, 0, 0.3)"
      : "0 4px 24px rgba(0, 0, 0, 0.08)",
  gap: `${theme.spacing(4)} !important`,
  marginTop: 0,
  color: theme.palette.text.secondary,
  fontSize: theme.typography.caption.fontSize,
  margin: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 20,
  minHeight: 64,
  [theme.breakpoints.up("md")]: {
    display: "none !important",
  },
  "& .MuiTabs-indicator": {
    display: "none",
  },
  "& .MuiTabs-flexContainer": {
    gap: 0,
  },
}));

interface StyledTabProps {
  $isactive: boolean;
}

export const StyledTab = styled(Tab, {
  shouldForwardProp: (prop) => prop !== "$isactive",
})<StyledTabProps>(({ $isactive, theme }) => ({
  color: `${
    $isactive ? theme.palette.primary.main : theme.palette.text.disabled
  } !important`,
  flex: 1,
  whiteSpace: "nowrap",
  fontSize: 9.5,
  fontWeight: $isactive ? 700 : 500,
  minHeight: 64,
  padding: "8px 4px 12px",
  position: "relative",
  transition: theme.transitions.create(["color", "font-weight"], {
    duration: theme.transitions.duration.shortest,
  }),
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 8,
    left: "50%",
    transform: "translateX(-50%)",
    width: $isactive ? 4 : 0,
    height: $isactive ? 4 : 0,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    transition: theme.transitions.create(["width", "height"], {
      duration: theme.transitions.duration.shortest,
    }),
  },
  "& .MuiTab-iconWrapper": {
    color: $isactive ? theme.palette.primary.main : theme.palette.text.disabled,
    width: "100%",
    height: 20,
    marginBottom: "2px !important",
    transition: theme.transitions.create(["color", "transform"], {
      duration: theme.transitions.duration.shortest,
    }),
    ...($isactive && {
      transform: "scale(1.15)",
    }),
    "& svg": {
      width: 22,
      height: 22,
    },
  },
}));

export const DashboardBottomNavigation = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [activeTab, setActiveTab] = useState(pathname);
  const { data: messagesData } = useMessageListQuery({ page: 1, limit: 1 });

  useEffect(() => {
    setActiveTab(pathname);
  }, [pathname]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    if (newValue === activeTab) {
      window.location.href = newValue;
      return;
    }

    setActiveTab(newValue);
    navigate(newValue);
  };

  const isJobHasAccess = user?.roles?.includes("approver");

  const BOTTOM_NAVIGATION_CONFIG = [
    {
      label: "خانه",
      icon: <LuHome />,
      value: paths.root,
      needsAccess: false,
    },
    {
      label: "درخواست ها",
      icon: <LuFilePlus />,
      value: paths.applications.list,
      needsAccess: false,
    },
    {
      label: "پیام‌ها",
      icon: <Badge badgeContent={messagesData?.unreadCount || 0} color="error" max={99}><MdEmail /></Badge>,
      value: paths.messages.list,
      needsAccess: false,
    },
    {
      label: "تقویم",
      icon: <LuCalendar />,
      value: paths.calendar.calendar,
      needsAccess: false,
    },
    {
      label: "اتاق جلسه",
      icon: <MdMeetingRoom />,
      value: paths.meetingRoom,
      needsAccess: false,
    },
    {
      label: "اعلانات",
      icon: <MdCampaign />,
      value: paths.announcements.list,
      needsAccess: false,
    },
    {
      label: "نظرسنجی",
      icon: <MdPoll />,
      value: paths.surveys.list,
      needsAccess: false,
    },
    {
      label: "حضور",
      icon: <MdAccessTime />,
      value: paths.attendance.today,
      needsAccess: false,
    },
    {
      label: "آموزش",
      icon: <MdSchool />,
      value: paths.training.list,
      needsAccess: false,
    },
    {
      label: "لیست درخواست ها",
      icon: <RiFileList3Line />,
      value: paths.pendingActions.list,
      needsAccess: true,
    },
  ];

  return (
    <StyledTabs
      value={activeTab}
      onChange={handleChange}
      aria-label="ant example"
      variant="scrollable"
      scrollButtons="auto"
    >
      {BOTTOM_NAVIGATION_CONFIG.map(({ value, label, icon, needsAccess }) => {
        if (needsAccess && !isJobHasAccess) return null;
        return (
          <StyledTab
            $isactive={value === activeTab}
            disableRipple={true}
            key={value}
            icon={icon}
            label={label}
            value={value}
          />
        );
      })}
    </StyledTabs>
  );
};
